import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    useCreatePointsGroup,
    CreatePointsGroupFormSchema,
    type CreatePointsGroupFormSchemaValues,
} from 'src/features/parkingManagement/api/PointsGroup/useCreatePointsGroup';
import { useDeletePointsGroup } from 'src/features/parkingManagement/api/PointsGroup/useDeletePointsGroup';
import { useUpdatePointsGroup } from 'src/features/parkingManagement/api/PointsGroup/useUpdatePointsGroup';
import PointsGroupForm from 'src/features/parkingManagement/components/PointsGroup/forms/PointsGroupForm';
import { type PointsGroup } from 'src/features/parkingManagement/types/pointsGroup';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface PointsGroupFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    isDialogOpen?: boolean;
    selectedPointsGroup?: PointsGroup;
    children?: React.ReactNode;
    isDelete?: boolean;
    removeTrigger?: boolean;
    onCloseDialog?: () => void;
}

export default function PointsGroupFormModal<TData>({
    isDialogOpen = false,
    selectedPointsGroup,
    className,
    semantics,
    children,
    isDelete,
    removeTrigger,
    onSubmit: updateTable,
    onCloseDialog,
}: PointsGroupFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();

    const { mutate: createPointsGroup, isPending: isPendingCreate } = useCreatePointsGroup({
        onSuccess: ({ name }: PointsGroup) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(generalMessages.successfulCreate),
                description: formatMessage(parkingManagementMessages.createPointsGroupSuccessfulDescription, {
                    name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updatePointsGroup, isPending: isPendingUpdate } = useUpdatePointsGroup({
        onSuccess: ({ name }: PointsGroup) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(generalMessages.successfulEdit),
                description: formatMessage(parkingManagementMessages.updatePointsGroupSuccessfulDescription, {
                    name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: deletePointsGroup, isPending: isPendingDelete } = useDeletePointsGroup({
        onSuccess: () => {
            if (updateTable) updateTable();
            toast({
                title: formatMessage(generalMessages.successfulDelete),
                description: formatMessage(parkingManagementMessages.deletePointsGroupSuccessfulDescription, {
                    name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { name = '', points = [] } = selectedPointsGroup || {};

    const form = useForm<CreatePointsGroupFormSchemaValues>({
        resolver: zodResolver(CreatePointsGroupFormSchema),
        defaultValues: { name, points },
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(selectedPointsGroup);
    }, [selectedPointsGroup]);

    const onSubmit = (values: CreatePointsGroupFormSchemaValues) => {
        const submitData = {
            parkingId: parkingId || '',
            ...values,
        };
        if (!selectedPointsGroup?.id) return createPointsGroup(submitData);
        if (isDelete && parkingId)
            return deletePointsGroup({ pointsGroupId: selectedPointsGroup.id, parkingId: parkingId });
        return updatePointsGroup({ pointsGroupId: selectedPointsGroup.id, ...submitData });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) setIsModalOpen(isOpen);
        if (!isOpen) {
            form.reset();
            if (onCloseDialog) onCloseDialog();
        }
    };

    const getDialogTitle = () => {
        if (isDelete) return formatMessage(parkingManagementMessages.deletePointsGroup);

        if (selectedPointsGroup?.id) return formatMessage(parkingManagementMessages.editPointsGroup);

        return formatMessage(tableMessages.new, {
            entry: semantics?.entryName?.toLowerCase(),
        });
    };

    const getDialogDescription = () => {
        if (isDelete) return formatMessage(parkingManagementMessages.deletePointsGroup);

        if (selectedPointsGroup?.id) return formatMessage(parkingManagementMessages.editPointsGroup);

        return formatMessage(parkingManagementMessages.createPointsGroup);
    };

    const getDialogIcon = () => {
        if (isDelete) return ICON_MAP.DELETE;
        if (selectedPointsGroup?.id) return ICON_MAP.EDIT;
        return ICON_MAP.ADD;
    };

    return (
        <Dialog modal open={isModalOpen || isDialogOpen} onOpenChange={onOpenChange}>
            {!children && !selectedPointsGroup && !removeTrigger && (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                        {formatMessage(tableMessages.new, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogTrigger>
            )}
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader className={cn(isDelete && 'flex-row gap-4')}>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={getDialogIcon()} />
                        <div>
                            <DialogTitle>{getDialogTitle()}</DialogTitle>
                            <DialogDescription>{getDialogDescription()}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />
                <DialogBody>
                    {isDelete ? (
                        <p className="text-gray-500">
                            {formatMessage(parkingManagementMessages.confirmDeletePointsGroup, {
                                name: selectedPointsGroup?.name,
                            })}
                        </p>
                    ) : (
                        <PointsGroupForm form={form} onSubmit={onSubmit} />
                    )}
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant={isDelete ? 'destructiveLight' : 'default'}
                        disabled={isPendingCreate || isPendingUpdate || isPendingDelete}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingCreate || isPendingUpdate || isPendingDelete ? (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(
                            isDelete
                                ? parkingManagementMessages.deletePointsGroup
                                : selectedPointsGroup?.id
                                  ? parkingManagementMessages.editPointsGroup
                                  : parkingManagementMessages.createPointsGroup,
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
